import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import MantencionesVehiculo from './MantencionesVehiculo';
import ContratistaSelector from './ContratistaSelector';

export default function VehiculosManager({ usuario, contratistaId = null }) {
    const [vehiculos, setVehiculos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedVehiculo, setSelectedVehiculo] = useState(null);
    const [expedienteData, setExpedienteData] = useState([]);
    const [loadingExpediente, setLoadingExpediente] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showMantenciones, setShowMantenciones] = useState(false);
    const [vehiculoMantencion, setVehiculoMantencion] = useState(null);

    const [showKmModal, setShowKmModal] = useState(false);
    const [kmVehiculo, setKmVehiculo] = useState(null);
    const [kmInput, setKmInput] = useState('');
    const [updatingKm, setUpdatingKm] = useState(false);
    const [pautas, setPautas] = useState([]);
    const [contratistas, setContratistas] = useState([]);
    const [selectedContratistaFilter, setSelectedContratistaFilter] = useState('');
    const [isGlobalMode, setIsGlobalMode] = useState(false);

    const targetContratistaId = contratistaId || usuario.id_contratista;
    const isSuperadmin = usuario?.rol_nombre === 'superadmin' || (!usuario?.id_contratista && usuario?.tipo !== 'C');

    const [formData, setFormData] = useState({
        patente: '',
        agno: '',
        marca: '',
        modelo: '',
        hasta: '',
        vin: '',
        chasis: '',
        motor: '',
        id_pauta: ''
    });

    useEffect(() => {
        if (isSuperadmin && !contratistaId) {
            setIsGlobalMode(true);
            fetchContratistas();
            fetchVehiculos();
        } else if (targetContratistaId) {
            setIsGlobalMode(false);
            fetchVehiculos();
        }
        fetchPautas();
    }, [targetContratistaId, isSuperadmin]);

    const fetchPautas = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/mantenciones/pautas`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setPautas(await response.json());
            }
        } catch (err) {
            console.error('Error cargando pautas', err);
        }
    };

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchContratistas = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas?empresa_id=${usuario.empresa_id}`);
            if (response.ok) setContratistas(await response.json());
        } catch (err) { console.error(err); }
    };

    const fetchVehiculos = async () => {
        try {
            const useGlobal = isSuperadmin && !contratistaId;
            const url = useGlobal
                ? `${API_BASE_URL}/api/vehiculos`
                : `${API_BASE_URL}/api/vehiculos?id_contratista=${targetContratistaId}`;
            const response = await fetchWithAuth(url);
            if (response.ok) {
                setVehiculos(await response.json());
                setError('');
            } else {
                setError('Error al cargar vehículos');
            }
        } catch (err) {
            setError('Error al cargar vehículos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        setLoading(true);
        try {
            const url = editingId
                ? `${API_BASE_URL}/api/vehiculos/${editingId}`
                : `${API_BASE_URL}/api/vehiculos`;

            const method = editingId ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, id_contratista: targetContratistaId })
            });

            if (response.ok) {
                fetchVehiculos();
                setShowForm(false);
                setEditingId(null);
                setFormData({ patente: '', agno: '', marca: '', modelo: '', hasta: '', vin: '', chasis: '', motor: '', id_pauta: '' });
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const handleEdit = (v) => {
        setEditingId(v.id);
        setFormData({
            patente: v.patente,
            agno: v.agno || '',
            marca: v.marca || '',
            modelo: v.modelo || '',
            hasta: v.hasta ? v.hasta.split('T')[0] : '',
            vin: v.vin || '',
            chasis: v.chasis || '',
            motor: v.motor || '',
            id_pauta: v.id_pauta || ''
        });
        setShowForm(true);
    };

    const handleOpenMantenciones = (vehiculo) => {
        setVehiculoMantencion(vehiculo);
        setShowMantenciones(true);
    };

    const handleOpenKmModal = (v) => {
        setKmVehiculo(v);
        setKmInput(v.kilometraje_actual != null ? String(v.kilometraje_actual) : '');
        setShowKmModal(true);
    };

    const handleGuardarKm = async () => {
        if (!kmVehiculo) return;
        const nuevo = parseInt(kmInput, 10);
        if (isNaN(nuevo) || nuevo < 0) {
            alert('Ingrese un kilometraje válido');
            return;
        }
        setUpdatingKm(true);
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/vehiculos/${kmVehiculo.id}/kilometraje`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ kilometraje: nuevo })
            });
            if (res.ok) {
                setShowKmModal(false);
                setKmVehiculo(null);
                setKmInput('');
                fetchVehiculos();
            } else {
                const data = await res.json();
                alert(data.error || 'Error al actualizar kilometraje');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setUpdatingKm(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este vehículo?')) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/vehiculos/${id}`, { method: 'DELETE' });
            if (response.ok) fetchVehiculos();
        } catch (err) {
            setError('Error al eliminar');
        }
    };

    const fetchExpediente = async (vehiculo) => {
        setLoadingExpediente(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos/entidad/V/${vehiculo.id}?empresa_id=${usuario.empresa_id}`);
            if (response.ok) {
                setExpedienteData(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar expediente:', err);
        } finally {
            setLoadingExpediente(false);
        }
    };

    const handleOpenExpediente = (vehiculo) => {
        setSelectedVehiculo(vehiculo);
        fetchExpediente(vehiculo);
    };

    const handleUploadDocument = async (tipoId, file, nombreDoc) => {
        if (!file) return;

        setUploadingDoc(tipoId);
        const data = new FormData();
        data.append('archivo', file);
        data.append('titulo', `${nombreDoc} - ${selectedVehiculo.patente}`);
        data.append('tipo_documento_id', tipoId);
        data.append('id_contratista', selectedVehiculo.id_contratista);
        data.append('entidad_id', selectedVehiculo.id);
        data.append('entidad_tipo', 'V');

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos`, {
                method: 'POST',
                body: data
            });

            if (response.ok) {
                alert('Documento subido y analizado con éxito');
                fetchExpediente(selectedVehiculo);
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al subir');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setUploadingDoc(null);
        }
    };

    return (
        <div className="manager-container">
            <div className="header-actions">
                <h2>Gestión de Vehículos</h2>
                <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ patente: '', agno: '', marca: '', modelo: '', hasta: '', vin: '', chasis: '', motor: '', id_pauta: '' }); }} className="add-button">
                    + Registrar Vehículo
                </button>
            </div>

            {isGlobalMode && (
                <div style={{ marginBottom: '20px' }}>
                    <ContratistaSelector
                        contratistas={contratistas}
                        value={selectedContratistaFilter}
                        onChange={setSelectedContratistaFilter}
                    />
                </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {showForm && (
                <div className="modal">
                    <form onSubmit={handleSubmit} className="standard-form" style={{ width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3 style={{ marginBottom: '24px', color: 'var(--sidebar-bg)', fontSize: '1.5rem' }}>
                            {editingId ? '📝 Editar' : '🚗 Nuevo'} Vehículo
                        </h3>

                        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                            <div className="form-group">
                                <label>Patente</label>
                                <input
                                    value={formData.patente}
                                    onChange={e => setFormData({ ...formData, patente: e.target.value.toUpperCase() })}
                                    placeholder="ABCD12"
                                    required
                                    style={{ fontSize: '1.1rem', fontWeight: '600', letterSpacing: '1px' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Año</label>
                                <input
                                    type="number"
                                    value={formData.agno}
                                    onChange={e => setFormData({ ...formData, agno: e.target.value })}
                                    placeholder="2024"
                                />
                            </div>
                            <div className="form-group">
                                <label>Marca</label>
                                <input
                                    value={formData.marca}
                                    onChange={e => setFormData({ ...formData, marca: e.target.value })}
                                    placeholder="Ej: Toyota"
                                />
                            </div>
                            <div className="form-group">
                                <label>Modelo</label>
                                <input
                                    value={formData.modelo}
                                    onChange={e => setFormData({ ...formData, modelo: e.target.value })}
                                    placeholder="Ej: Hilux"
                                />
                            </div>
                            <div className="form-group">
                                <label>VIN</label>
                                <input
                                    value={formData.vin}
                                    onChange={e => setFormData({ ...formData, vin: e.target.value })}
                                    placeholder="Número VIN"
                                />
                            </div>
                            <div className="form-group">
                                <label>Chasis</label>
                                <input
                                    value={formData.chasis}
                                    onChange={e => setFormData({ ...formData, chasis: e.target.value })}
                                    placeholder="Número de Chasis"
                                />
                            </div>
                            <div className="form-group">
                                <label>Motor</label>
                                <input
                                    value={formData.motor}
                                    onChange={e => setFormData({ ...formData, motor: e.target.value })}
                                    placeholder="Número de Motor"
                                />
                            </div>
                            <div className="form-group">
                                <label>Pauta de Mantención (Opcional)</label>
                                <select 
                                    value={formData.id_pauta}
                                    onChange={e => setFormData({ ...formData, id_pauta: e.target.value })}
                                >
                                    <option value="">-- Sin pauta asignada --</option>
                                    {pautas.map(p => (
                                        <option key={p.id} value={p.id}>{p.marca} {p.modelo} - {p.descripcion}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
                            <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Vigencia Pertenencia</span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: '400', color: 'var(--text-muted)' }}>Opcional</span>
                                </label>
                                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
                                    Indique fecha solo si el vehículo ya no pertenece al contratista.
                                </p>
                                <input
                                    type="date"
                                    value={formData.hasta}
                                    onChange={e => setFormData({ ...formData, hasta: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-actions" style={{ marginTop: '32px' }}>
                            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading || isSubmitting} className="add-button" style={{ padding: '12px 40px' }}>
                                {editingId ? 'Actualizar Vehículo' : 'Guardar Vehículo'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {selectedVehiculo && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div>
                                <h3 style={{ color: 'var(--sidebar-bg)', fontSize: '1.5rem', marginBottom: '4px' }}>
                                    📂 Expediente: {selectedVehiculo.patente}
                                </h3>
                                <p style={{ color: 'var(--text-muted)' }}>{selectedVehiculo.marca} {selectedVehiculo.modelo} ({selectedVehiculo.agno})</p>
                            </div>
                            <button onClick={() => setSelectedVehiculo(null)} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>

                        {loadingExpediente ? <p>Cargando documentos requeridos...</p> : (
                            <table className="standard-table">
                                <thead>
                                    <tr>
                                        <th>Documento Requerido</th>
                                        <th>Estado</th>
                                        <th>Última Carga</th>
                                        <th>Vencimiento</th>
                                        <th>Acción / Resultado IA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expedienteData.map(doc => (
                                        <tr key={doc.tipo_id}>
                                            <td data-label="Documento" style={{ fontWeight: '500' }}>
                                                {doc.nombre}
                                                {doc.descripcion && <div style={{ fontSize: '0.75rem', fontWeight: '400', color: 'var(--text-muted)' }}>{doc.descripcion}</div>}
                                            </td>
                                            <td data-label="Estado">
                                                <span className={`status-badge ${doc.estado.toLowerCase()}`} style={{ fontSize: '0.7rem' }}>
                                                    {doc.estado}
                                                </span>
                                            </td>
                                            <td data-label="Última Carga" style={{ fontSize: '0.85rem' }}>
                                                {doc.fecha ? new Date(doc.fecha).toLocaleDateString() : '---'}
                                            </td>
                                            <td data-label="Vencimiento" style={{ fontSize: '0.85rem', color: doc.estado === 'VENCIDO' ? '#ea580c' : 'inherit', fontWeight: doc.estado === 'VENCIDO' ? '600' : '400' }}>
                                                {doc.vencimiento ? new Date(doc.vencimiento).toLocaleDateString() : '---'}
                                            </td>
                                            <td data-label="Acciones">
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                                    {doc.detalles && (
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: '250px', fontStyle: 'italic', textAlign: 'right' }}>
                                                            "{doc.detalles}"
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.png,.jpg,.jpeg"
                                                            id={`upload-${doc.tipo_id}`}
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => handleUploadDocument(doc.tipo_id, e.target.files[0], doc.nombre)}
                                                        />
                                                        <button
                                                            disabled={uploadingDoc !== null}
                                                            onClick={() => document.getElementById(`upload-${doc.tipo_id}`).click()}
                                                            className="btn-action btn-edit"
                                                            style={{ fontSize: '0.75rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        >
                                                            {uploadingDoc === doc.tipo_id ? (
                                                                <>
                                                                    <span className="spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', marginRight: '0' }}></span>
                                                                    Procesando...
                                                                </>
                                                            ) : (doc.documento_id ? '🔄 Reemplazar' : '📤 Subir')}
                                                        </button>

                                                        {doc.nombre_archivo && (
                                                            <a
                                                                href={`${API_BASE_URL}/uploads/${doc.nombre_archivo}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: '600', whiteSpace: 'nowrap' }}
                                                            >
                                                                📂 Ver Documento
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className="form-actions" style={{ marginTop: '24px' }}>
                            <button type="button" onClick={() => setSelectedVehiculo(null)} className="btn-secondary">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? <p>Cargando vehículos...</p> : (
                <table className="standard-table">
                    <thead>
                        <tr>
                            {isGlobalMode && <th>Contratista</th>}
                            <th>Patente</th>
                            <th>Vehículo</th>
                            <th>Identificadores (VIN/Chasis/Motor)</th>
                            <th>Estado Pertenencia</th>
                            <th>Mantención</th>
                            <th>Documentación</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vehiculos.filter(v => !isGlobalMode || !selectedContratistaFilter || v.id_contratista === selectedContratistaFilter).map(v => (
                            <tr key={v.id}>
                                {isGlobalMode && <td data-label="Contratista" style={{ fontWeight: '600', fontSize: '0.85rem' }}>{v.contratista_nombre || '---'}</td>}
                                <td data-label="Patente" style={{ fontWeight: '700', color: 'var(--primary)' }}>{v.patente}</td>
                                <td data-label="Vehículo">
                                    <div>{v.marca} {v.modelo}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>Año: {v.agno || '---'}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '600' }}>
                                        🛣️ {v.kilometraje_actual != null ? `${Number(v.kilometraje_actual).toLocaleString()} KM` : 'Sin KM'}
                                    </div>
                                </td>
                                <td data-label="Identificadores">
                                    <div style={{ fontSize: '0.85rem' }}>
                                        {v.vin && <div><small>VIN:</small> {v.vin}</div>}
                                        {v.chasis && <div><small>Chas:</small> {v.chasis}</div>}
                                        {v.motor && <div><small>Mot:</small> {v.motor}</div>}
                                        {!v.vin && !v.chasis && !v.motor && '---'}
                                    </div>
                                </td>
                                <td data-label="Pertenencia">
                                    {v.hasta ? (
                                        <span className="status-badge invalid" style={{ fontSize: '0.7rem' }}>
                                            Hasta: {new Date(v.hasta).toLocaleDateString()}
                                        </span>
                                    ) : (
                                        <span className="status-badge valid" style={{ fontSize: '0.7rem' }}>
                                            Vigente
                                        </span>
                                    )}
                                </td>
                                <td data-label="Mantención">
                                    {(() => {
                                        const m = v.mantencion;
                                        if (!m || m.semaforo === 'none') {
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title={m?.detalle || 'Sin pauta asignada'}>
                                                    <span style={{ display: 'inline-block', width: '16px', height: '16px', borderRadius: '50%', background: '#94a3b8', border: '2px solid #64748b', flexShrink: 0 }} />
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m?.detalle || 'Sin pauta'}</span>
                                                </div>
                                            );
                                        }
                                        const colores = { verde: '#22c55e', amarillo: '#eab308', rojo: '#ef4444' };
                                        const bordes = { verde: '#15803d', amarillo: '#a16207', rojo: '#b91c1c' };
                                        const textos = { verde: '#15803d', amarillo: '#a16207', rojo: '#b91c1c' };
                                        const kmFaltan = Math.abs(m.km_restantes).toLocaleString();
                                        const motivo = m.semaforo === 'rojo'
                                            ? `Sobrepasada por ${kmFaltan} KM`
                                            : `Faltan ${kmFaltan} KM`;
                                        const detalle = `${m.proximo_hito} a los ${m.proximo_km?.toLocaleString()} KM — ${motivo}`;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title={detalle}>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        background: colores[m.semaforo],
                                                        border: `2px solid ${bordes[m.semaforo]}`,
                                                        boxShadow: `0 0 6px ${colores[m.semaforo]}`,
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <div style={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                                                    <div style={{ fontWeight: '600', color: textos[m.semaforo] }}>{motivo}</div>
                                                    <div style={{ color: 'var(--text-muted)' }}>{m.proximo_hito} a los {m.proximo_km?.toLocaleString()} KM</div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td data-label="Documentación">
                                    {v.documentacion ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                            <span className={`status-badge ${v.documentacion.estado_general === 'COMPLETO' ? 'valid' :
                                                v.documentacion.estado_general === 'VENCIDO' ? 'vencido' :
                                                    (v.documentacion.estado_general === 'PENDIENTE' ? 'warning' : 'invalid')
                                                }`} style={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                {v.documentacion.estado_general}
                                            </span>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                                                {v.documentacion.aprobados}/{v.documentacion.total} Aprobados
                                                {v.documentacion.vencidos > 0 && <span style={{ color: '#ea580c', marginLeft: '4px' }}>({v.documentacion.vencidos} Venc.)</span>}
                                                {v.documentacion.rechazados > 0 && <span style={{ color: 'var(--error)', marginLeft: '4px' }}>({v.documentacion.rechazados} Rech.)</span>}
                                            </div>
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cargando...</span>
                                    )}
                                </td>
                                <td data-label="Acciones">
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                        <button onClick={() => handleOpenExpediente(v)} className="btn-action btn-details" title="Ver Expediente de Documentos">
                                            {(loadingExpediente || uploadingDoc) && selectedVehiculo?.id === v.id ? (
                                                <span className="spinner"></span>
                                            ) : '📂 '}
                                            Expediente
                                        </button>
                                        <button onClick={() => handleEdit(v)} className="btn-action btn-edit" title="Editar Vehículo">📝</button>
                                        <button onClick={() => handleOpenMantenciones(v)} className="btn-action btn-details" title="Historial Mantenciones">🛠️ Mantenciones</button>
                                        <button onClick={() => handleOpenKmModal(v)} className="btn-action" style={{ background: '#ecfeff', color: '#155e75', border: '1px solid #a5f3fc' }} title="Actualizar kilometraje">🛣️ KM</button>
                                        <button onClick={() => handleDelete(v.id)} className="btn-action btn-delete" title="Eliminar Vehículo">🗑️</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {vehiculos.filter(v => !isGlobalMode || !selectedContratistaFilter || v.id_contratista === selectedContratistaFilter).length === 0 && (
                            <tr><td colSpan={isGlobalMode ? 8 : 7} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>No hay vehículos registrados.</td></tr>
                        )}
                    </tbody>
                </table>
            )}

            {/* Modal de Mantenciones */}
            {showMantenciones && vehiculoMantencion && (
                <MantencionesVehiculo 
                    vehiculo={vehiculoMantencion} 
                    usuario={usuario} 
                    onClose={() => {
                        setShowMantenciones(false);
                        setVehiculoMantencion(null);
                        fetchVehiculos(); // Recargar para actualizar KM actual si cambió
                    }} 
                />
            )}

            {/* Modal actualizar kilometraje */}
            {showKmModal && kmVehiculo && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '400px' }}>
                        <h3 style={{ marginBottom: '8px', color: 'var(--sidebar-bg)' }}>
                            🛣️ Actualizar kilometraje
                        </h3>
                        <p style={{ marginBottom: '16px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                            {kmVehiculo.marca} {kmVehiculo.modelo} — Patente {kmVehiculo.patente}
                        </p>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Kilometraje actual</label>
                            <input
                                type="number"
                                min={kmVehiculo.kilometraje_actual != null ? kmVehiculo.kilometraje_actual : 0}
                                value={kmInput}
                                onChange={(e) => setKmInput(e.target.value)}
                                placeholder="Ej: 45200"
                            />
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
                                KM registrado: {kmVehiculo.kilometraje_actual != null ? `${Number(kmVehiculo.kilometraje_actual).toLocaleString()} KM` : 'Sin KM'}
                            </p>
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={() => { setShowKmModal(false); setKmVehiculo(null); setKmInput(''); }} className="btn-secondary">
                                Cancelar
                            </button>
                            <button
                                onClick={handleGuardarKm}
                                disabled={updatingKm || !kmInput}
                                className="btn-primary"
                            >
                                {updatingKm ? (
                                    <>
                                        <span className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px', marginRight: '8px' }}></span>
                                        Guardando...
                                    </>
                                ) : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
